import { Link } from "react-router-dom";
import { isLastChance, type EventStatus } from "@/lib/events";

interface EventStatusBadgeProps {
  status: EventStatus;
  hasRegistrationLinks?: boolean;
  eventUrl?: string;
  dateFinish?: string;
  isVirtual?: boolean;
  /** Per-event override: leg starts within 7 days. When provided, drives the
   *  urgency pill directly instead of the dateFinish/isLastChance heuristic. */
  startsSoon?: boolean;
}

const badgeClasses =
  "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border";

type BadgeCfg = { label: string; className: string };

const IN_PROGRESS: BadgeCfg = {
  label: "In Progress",
  className:
    "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border-amber-200/60 dark:border-amber-800/40 animate-pulse-subtle dark:animate-none",
};

const COMPLETED: BadgeCfg = {
  label: "Completed",
  className:
    "bg-gray-100 text-gray-500 dark:bg-gray-800/40 dark:text-gray-400 border-gray-200/60 dark:border-gray-700/40",
};

const urgencyBadge = (isVirtual?: boolean): BadgeCfg => ({
  label: isVirtual ? "\uD83D\uDD25 Last chance" : "\u26A1 Almost here",
  className:
    "bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400 border-orange-200/60 dark:border-orange-800/40 animate-pulse-subtle dark:animate-none",
});

/**
 * Contextual status pill for a quizrun leg. Registration-open is the default
 * state for every joinable leg, so we render NOTHING for it — silence means
 * open. A pill appears only when the state is exceptional:
 *   - not started yet but ending within 7 days → "Almost here"/"Last chance"
 *   - event window live (in-progress) → "In Progress"
 *   - finished → grey "Completed"
 * This keeps the row calm: at most one pill, and usually none.
 */
export const EventStatusBadge = ({
  status,
  eventUrl,
  dateFinish,
  isVirtual,
  startsSoon,
}: EventStatusBadgeProps) => {
  const renderBadge = (cfg: BadgeCfg, asLink: boolean, key?: string) => {
    if (asLink && eventUrl) {
      return (
        <Link
          key={key}
          to={eventUrl}
          className={`${badgeClasses} ${cfg.className} hover:opacity-80 transition-opacity`}
        >
          {cfg.label}
        </Link>
      );
    }
    return (
      <span key={key} className={`${badgeClasses} ${cfg.className}`}>
        {cfg.label}
      </span>
    );
  };

  if (status === "completed") {
    return renderBadge(COMPLETED, false);
  }

  // Per-event rows pass startsSoon (urgency = leg beginning soon); quizrun-level
  // callers fall back to the dateFinish/isLastChance heuristic (window ending).
  const urgent = startsSoon ?? isLastChance(dateFinish);

  // Only exceptional states get a pill; a plain open leg renders nothing.
  let contextual: BadgeCfg | null = null;
  if (status === "registration-open" && urgent) {
    contextual = urgencyBadge(isVirtual);
  } else if (status === "in-progress") {
    contextual = IN_PROGRESS;
  }

  if (!contextual) return null;
  return renderBadge(contextual, true, "ctx");
};
